import {
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import PostalCodeMaskInput from '@/components/PostalCodeMaskInput';
import { canadianProvinces, countries, USAStates } from '@/utils/geo';

/** @param {import("prop-types").InferProps<typeof StepReceiptInfoPropTypes>} props */
export default function StepReceiptInfo({ formData, setFormData, nextStep, prevStep }) {
  const { t, i18n } = useTranslation();

  const handleChange = event => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleNext = () => {
    nextStep();
  };

  const isCanada = formData.userCountry === 'CA';
  const isUnitedStates = formData.userCountry === 'US';

  /** @type {import('@mui/material').TextFieldProps} */
  const postalConfig = (() => {
    if (isCanada) {
      return {
        label: t('donation.postalCode'),
        helperText: 'Format H0H 0H0',
        slotProps: {
          input: {
            inputComponent: PostalCodeMaskInput,
            inputProps: { country: 'CA' },
          },
        },
      };
    }
    if (isUnitedStates) {
      return {
        label: t('donation.zipCode'),
        helperText: 'Format 90210',
        slotProps: {
          input: {
            inputComponent: PostalCodeMaskInput,
            inputProps: { country: 'US' },
          },
        },
      };
    }

    return {
      label: t('donation.postalCode'),
    };
  })();

  return (
    <Box
      component="form"
      onSubmit={event => {
        event.preventDefault();
        handleNext();
      }}
    >
      <Typography gutterBottom variant="h5">
        {t('donation.stepReceipt')}
      </Typography>

      <Grid container spacing={2} sx={{ marginTop: 1 }}>
        <Grid size={12}>
          <FormLabel>{t('donation.donorType')}</FormLabel>
          <RadioGroup
            row
            name="donorType"
            value={formData.donorType || 'person'}
            onChange={handleChange}
          >
            <FormControlLabel value="person" control={<Radio />} label={t('donation.person')} />
            <FormControlLabel value="company" control={<Radio />} label={t('donation.company')} />
          </RadioGroup>
        </Grid>

        <Grid size={12}>
          <TextField
            required
            fullWidth
            label={t('donation.fullName')}
            name="userFullName"
            value={formData.userFullName || ''}
            onChange={handleChange}
          />
        </Grid>

        {formData.donorType === 'company' && (
          <Grid size={12}>
            <TextField
              fullWidth
              required={formData.donorType === 'company'}
              label={t('donation.companyName')}
              name="userCompany"
              value={formData.userCompany || ''}
              onChange={handleChange}
            />
          </Grid>
        )}

        <Grid size={12}>
          <TextField
            required
            fullWidth
            label={t('donation.email')}
            name="userEmail"
            type="email"
            inputProps={{ pattern: String.raw`[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}` }}
            value={formData.userEmail}
            onChange={handleChange}
            title={t('donation.emailPatternHint')}
          />
        </Grid>

        <Grid size={12}>
          <TextField
            required
            fullWidth
            label={t('donation.address')}
            name="userAddress"
            value={formData.userAddress}
            onChange={handleChange}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            required
            fullWidth
            label={t('donation.city')}
            name="userCity"
            value={formData.userCity}
            onChange={handleChange}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <Autocomplete
            disableClearable
            options={countries}
            getOptionLabel={opt => opt[i18n.language] || opt.en}
            value={countries.find(country => country.code === formData.userCountry) || null}
            onChange={(_, newCountry) =>
              setFormData(prev => ({
                ...prev,
                userCountry: newCountry?.code || '',
                userProvince: '', // reset province when country changes
              }))
            }
            renderInput={params => (
              <TextField {...params} required fullWidth label={t('donation.country')} />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            required
            fullWidth
            name="userPostalCode"
            value={formData.userPostalCode || ''}
            onChange={handleChange}
            {...postalConfig}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          {isCanada && (
            <Autocomplete
              disableClearable
              options={canadianProvinces}
              getOptionLabel={opt => opt[i18n.language] || opt.en}
              value={
                canadianProvinces.find(province => province.code === formData.userProvince) || null
              }
              onChange={(_, newProv) =>
                setFormData(prev => ({
                  ...prev,
                  userProvince: newProv?.code || '',
                }))
              }
              renderInput={params => (
                <TextField {...params} required fullWidth label={t('donation.province')} />
              )}
            />
          )}
          {isUnitedStates && (
            <Autocomplete
              disableClearable
              options={USAStates}
              getOptionLabel={opt => opt[i18n.language] || opt.en}
              value={USAStates.find(state => state.code === formData.userProvince) || null}
              onChange={(_, newState) =>
                setFormData(prev => ({
                  ...prev,
                  userProvince: newState?.code || '',
                }))
              }
              renderInput={params => (
                <TextField {...params} required fullWidth label={t('donation.state')} />
              )}
            />
          )}
          {!isCanada && !isUnitedStates && (
            <TextField
              required
              fullWidth
              label={t('donation.province')}
              name="userProvince"
              value={formData.userProvince}
              onChange={handleChange}
            />
          )}
        </Grid>
      </Grid>

      <Box mt={4} sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="outlined" onClick={prevStep}>
          {t('common.back')}
        </Button>
        <Button type="submit" variant="contained">
          {t('common.continue')}
        </Button>
      </Box>
    </Box>
  );
}

const StepReceiptInfoPropTypes = {
  formData: PropTypes.shape({
    donorType: PropTypes.string,
    userFullName: PropTypes.string,
    userCompany: PropTypes.string,
    userEmail: PropTypes.string,
    userAddress: PropTypes.string,
    userCity: PropTypes.string,
    userPostalCode: PropTypes.string,
    userCountry: PropTypes.string,
    userProvince: PropTypes.string,
  }).isRequired,
  nextStep: PropTypes.func.isRequired,
  prevStep: PropTypes.func.isRequired,
  setFormData: PropTypes.func.isRequired,
};
StepReceiptInfo.propTypes = StepReceiptInfoPropTypes;
